"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/alert-dialog'
import { LucideIcon, OctagonAlert, CircleFadingArrowUp, Info, AlertTriangle } from 'lucide-react'
import { UIBox } from "../layout/ui-box"
import { UIBadge } from "../display/ui-badge"
import { UIButton } from "../buttons/ui-button"

// ========================================
// Base Components (shadcn/ui 패턴 그대로 노출)
// ========================================

/**
 * UIAlertDialog - 기본 알럿 다이얼로그 컨테이너
 *
 * shadcn/ui AlertDialog를 그대로 노출하여 최대한의 유연성을 제공합니다.
 *
 * @example
 * ```tsx
 * <UIAlertDialog>
 *   <UIAlertDialogTrigger asChild>
 *     <UIButton>확인</UIButton>
 *   </UIAlertDialogTrigger>
 *   <UIAlertDialogContent>
 *     <UIAlertDialogHeader>
 *       <UIAlertDialogTitle>정말 삭제하시겠습니까?</UIAlertDialogTitle>
 *       <UIAlertDialogDescription>이 작업은 되돌릴 수 없습니다.</UIAlertDialogDescription>
 *     </UIAlertDialogHeader>
 *     <UIAlertDialogFooter>
 *       <UIAlertDialogCancel>취소</UIAlertDialogCancel>
 *       <UIAlertDialogAction>삭제</UIAlertDialogAction>
 *     </UIAlertDialogFooter>
 *   </UIAlertDialogContent>
 * </UIAlertDialog>
 * ```
 */
export const UIAlertDialog: React.FC<React.ComponentProps<typeof AlertDialog>> = (props) => {
  return <AlertDialog {...props} />
}

UIAlertDialog.displayName = "UIAlertDialog"

/**
 * UIAlertDialogTrigger - 알럿 다이얼로그 트리거
 */
export const UIAlertDialogTrigger = React.forwardRef<
  React.ComponentRef<typeof AlertDialogTrigger>,
  React.ComponentPropsWithoutRef<typeof AlertDialogTrigger>
>((props, ref) => {
  return <AlertDialogTrigger ref={ref} {...props} />
})

UIAlertDialogTrigger.displayName = "UIAlertDialogTrigger"

/**
 * UIAlertDialogContent - 알럿 다이얼로그 콘텐츠
 */
export const UIAlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogContent>,
  React.ComponentPropsWithoutRef<typeof AlertDialogContent>
>(({ className, ...props }, ref) => {
  return (
    <AlertDialogContent
      ref={ref}
      className={cn(
        "w-full max-w-full min-w-0 flex-shrink-0", // 🔧 Width stability 패턴 적용
        className
      )}
      {...props}
    />
  )
})

UIAlertDialogContent.displayName = "UIAlertDialogContent"

/**
 * UIAlertDialogHeader - 알럿 다이얼로그 헤더
 */
export const UIAlertDialogHeader = React.forwardRef<
  React.ComponentRef<typeof AlertDialogHeader>,
  React.ComponentPropsWithoutRef<typeof AlertDialogHeader>
>((props, ref) => {
  return <AlertDialogHeader ref={ref} {...props} />
})

UIAlertDialogHeader.displayName = "UIAlertDialogHeader"

/**
 * UIAlertDialogFooter - 알럿 다이얼로그 푸터
 */
export const UIAlertDialogFooter = React.forwardRef<
  React.ComponentRef<typeof AlertDialogFooter>,
  React.ComponentPropsWithoutRef<typeof AlertDialogFooter>
>((props, ref) => {
  return <AlertDialogFooter ref={ref} {...props} />
})

UIAlertDialogFooter.displayName = "UIAlertDialogFooter"

/**
 * UIAlertDialogTitle - 알럿 다이얼로그 제목
 */
export const UIAlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogTitle>,
  React.ComponentPropsWithoutRef<typeof AlertDialogTitle>
>((props, ref) => {
  return <AlertDialogTitle ref={ref} {...props} />
})

UIAlertDialogTitle.displayName = "UIAlertDialogTitle"

/**
 * UIAlertDialogDescription - 알럿 다이얼로그 설명
 */
export const UIAlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogDescription>,
  React.ComponentPropsWithoutRef<typeof AlertDialogDescription>
>((props, ref) => {
  return <AlertDialogDescription ref={ref} {...props} />
})

UIAlertDialogDescription.displayName = "UIAlertDialogDescription"

/**
 * UIAlertDialogAction - 알럿 다이얼로그 액션 버튼
 */
export const UIAlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogAction>,
  React.ComponentPropsWithoutRef<typeof AlertDialogAction>
>((props, ref) => {
  return <AlertDialogAction ref={ref} {...props} />
})

UIAlertDialogAction.displayName = "UIAlertDialogAction"

/**
 * UIAlertDialogCancel - 알럿 다이얼로그 취소 버튼
 */
export const UIAlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogCancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogCancel>
>((props, ref) => {
  return <AlertDialogCancel ref={ref} {...props} />
})

UIAlertDialogCancel.displayName = "UIAlertDialogCancel"

// ========================================
// Preset Components (shadcn-ui-blocks 4가지 변형)
// ========================================

const alertDialogPresetVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0", // 🔧 Width stability 패턴
  {
    variants: {
      variant: {
        default: "sm:max-w-lg",
        icon: "sm:max-w-md text-center",
        destructive: "sm:max-w-md text-center",
        info: "sm:max-w-lg",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface UIAlertDialogPresetProps extends VariantProps<typeof alertDialogPresetVariants> {
  /**
   * 다이얼로그가 열려있는지 여부
   */
  open?: boolean
  /**
   * 다이얼로그 열림/닫힘 상태 변경 콜백
   */
  onOpenChange?: (open: boolean) => void
  /**
   * 다이얼로그 제목
   */
  title: string
  /**
   * 다이얼로그 설명
   */
  description: string
  /**
   * 취소 버튼 텍스트
   */
  cancelText?: string
  /**
   * 확인 버튼 텍스트
   */
  confirmText?: string
  /**
   * 취소 버튼 클릭 콜백
   */
  onCancel?: () => void
  /**
   * 확인 버튼 클릭 콜백
   */
  onConfirm?: () => void
  /**
   * 트리거 요소 (children)
   */
  children?: React.ReactNode
  /**
   * 아이콘 (icon 및 destructive variant용)
   */
  icon?: LucideIcon
  /**
   * 배지 목록 (info variant용)
   */
  badges?: string[]
  /**
   * 추가 CSS 클래스
   */
  className?: string
}

/**
 * UIAlertDialogDefault - 기본 확인 다이얼로그
 *
 * shadcn-ui-blocks의 Default Dialog 패턴을 구현한 프리셋 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <UIAlertDialogDefault
 *   title="계정을 삭제하시겠습니까?"
 *   description="이 작업은 되돌릴 수 없습니다. 모든 데이터가 영구적으로 삭제됩니다."
 *   cancelText="취소"
 *   confirmText="삭제"
 *   onConfirm={() => console.log('삭제 확인')}
 * >
 *   <UIButton>계정 삭제</UIButton>
 * </UIAlertDialogDefault>
 * ```
 */
export const UIAlertDialogDefault: React.FC<UIAlertDialogPresetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  cancelText = "취소",
  confirmText = "확인",
  onCancel,
  onConfirm,
  children,
  className,
  variant: _variant,
  icon: _icon,
  badges: _badges,
  ...props
}) => {
  return (
    <UIAlertDialog open={open} onOpenChange={onOpenChange} {...props}>
      {children && (
        <UIAlertDialogTrigger asChild>
          {children}
        </UIAlertDialogTrigger>
      )}
      <UIAlertDialogContent className={cn(alertDialogPresetVariants({ variant: "default" }), className)}>
        <UIAlertDialogHeader>
          <UIAlertDialogTitle>{title}</UIAlertDialogTitle>
          <UIAlertDialogDescription>{description}</UIAlertDialogDescription>
        </UIAlertDialogHeader>
        <UIAlertDialogFooter>
          <UIAlertDialogCancel onClick={onCancel}>{cancelText}</UIAlertDialogCancel>
          <UIAlertDialogAction onClick={onConfirm}>{confirmText}</UIAlertDialogAction>
        </UIAlertDialogFooter>
      </UIAlertDialogContent>
    </UIAlertDialog>
  )
}

UIAlertDialogDefault.displayName = "UIAlertDialogDefault"

/**
 * UIAlertDialogIcon - 아이콘이 포함된 강조 다이얼로그
 *
 * shadcn-ui-blocks의 Dialog with Icon 패턴을 구현한 프리셋 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <UIAlertDialogIcon
 *   icon={AlertTriangle}
 *   title="중요한 변경사항"
 *   description="이 변경사항은 모든 사용자에게 영향을 줍니다."
 *   confirmText="적용"
 *   onConfirm={() => console.log('변경사항 적용')}
 * >
 *   <UIButton>변경사항 적용</UIButton>
 * </UIAlertDialogIcon>
 * ```
 */
export const UIAlertDialogIcon: React.FC<UIAlertDialogPresetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  cancelText = "취소",
  confirmText = "확인",
  onCancel,
  onConfirm,
  children,
  icon: Icon = OctagonAlert,
  className,
  variant: _variant,
  badges: _badges,
  ...props
}) => {
  return (
    <UIAlertDialog open={open} onOpenChange={onOpenChange} {...props}>
      {children && (
        <UIAlertDialogTrigger asChild>
          {children}
        </UIAlertDialogTrigger>
      )}
      <UIAlertDialogContent className={cn(alertDialogPresetVariants({ variant: "icon" }), className)}>
        <UIAlertDialogHeader className="items-center">
          <UIBox className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          </UIBox>
          <UIAlertDialogTitle>{title}</UIAlertDialogTitle>
          <UIAlertDialogDescription>{description}</UIAlertDialogDescription>
        </UIAlertDialogHeader>
        <UIAlertDialogFooter className="sm:justify-center">
          <UIAlertDialogCancel onClick={onCancel}>{cancelText}</UIAlertDialogCancel>
          <UIAlertDialogAction onClick={onConfirm}>{confirmText}</UIAlertDialogAction>
        </UIAlertDialogFooter>
      </UIAlertDialogContent>
    </UIAlertDialog>
  )
}

UIAlertDialogIcon.displayName = "UIAlertDialogIcon"

/**
 * UIAlertDialogDestructive - 위험한 작업용 경고 다이얼로그
 *
 * shadcn-ui-blocks의 Destructive Dialog 패턴을 구현한 프리셋 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <UIAlertDialogDestructive
 *   title="데이터 삭제"
 *   description="선택한 데이터를 영구적으로 삭제합니다. 이 작업은 되돌릴 수 없습니다."
 *   confirmText="삭제"
 *   onConfirm={() => console.log('데이터 삭제')}
 * >
 *   <UIButton className="bg-red-600 text-white">데이터 삭제</UIButton>
 * </UIAlertDialogDestructive>
 * ```
 */
export const UIAlertDialogDestructive: React.FC<UIAlertDialogPresetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  cancelText = "취소",
  confirmText = "삭제",
  onCancel,
  onConfirm,
  children,
  icon: Icon = AlertTriangle,
  className,
  variant: _variant,
  badges: _badges,
  ...props
}) => {
  return (
    <UIAlertDialog open={open} onOpenChange={onOpenChange} {...props}>
      {children && (
        <UIAlertDialogTrigger asChild>
          {children}
        </UIAlertDialogTrigger>
      )}
      <UIAlertDialogContent className={cn(alertDialogPresetVariants({ variant: "destructive" }), className)}>
        <UIAlertDialogHeader className="items-center">
          <UIBox className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-red-600 dark:text-red-400" />
          </UIBox>
          <UIAlertDialogTitle className="text-red-900 dark:text-red-100">{title}</UIAlertDialogTitle>
          <UIAlertDialogDescription>{description}</UIAlertDialogDescription>
        </UIAlertDialogHeader>
        <UIAlertDialogFooter className="sm:justify-center">
          <UIAlertDialogCancel onClick={onCancel}>{cancelText}</UIAlertDialogCancel>
          <UIAlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
          >
            {confirmText}
          </UIAlertDialogAction>
        </UIAlertDialogFooter>
      </UIAlertDialogContent>
    </UIAlertDialog>
  )
}

UIAlertDialogDestructive.displayName = "UIAlertDialogDestructive"

/**
 * UIAlertDialogInfo - 정보 제공용 배지 포함 다이얼로그
 *
 * shadcn-ui-blocks의 Info Dialog 패턴을 구현한 프리셋 컴포넌트입니다.
 *
 * @example
 * ```tsx
 * <UIAlertDialogInfo
 *   title="새로운 기능 업데이트"
 *   description="앱에 새로운 기능들이 추가되었습니다."
 *   badges={["새로운 UI", "성능 개선", "버그 수정"]}
 *   confirmText="업데이트"
 *   onConfirm={() => console.log('업데이트 시작')}
 * >
 *   <UIButton>업데이트 확인</UIButton>
 * </UIAlertDialogInfo>
 * ```
 */
export const UIAlertDialogInfo: React.FC<UIAlertDialogPresetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  cancelText = "나중에",
  confirmText = "확인",
  onCancel,
  onConfirm,
  children,
  icon: Icon = CircleFadingArrowUp,
  badges = [],
  className,
  variant: _variant,
  ...props
}) => {
  return (
    <UIAlertDialog open={open} onOpenChange={onOpenChange} {...props}>
      {children && (
        <UIAlertDialogTrigger asChild>
          {children}
        </UIAlertDialogTrigger>
      )}
      <UIAlertDialogContent className={cn(alertDialogPresetVariants({ variant: "info" }), className)}>
        <UIAlertDialogHeader>
          <UIBox className="flex items-center gap-3 mb-4">
            <UIBox className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </UIBox>
            <UIAlertDialogTitle>{title}</UIAlertDialogTitle>
          </UIBox>
          <UIAlertDialogDescription className="text-left">{description}</UIAlertDialogDescription>

          {badges.length > 0 && (
            <UIBox className="flex flex-wrap gap-2 mt-4">
              {badges.map((badge, index) => (
                <UIBadge
                  key={index}
                  variant="secondary"
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                >
                  {badge}
                </UIBadge>
              ))}
            </UIBox>
          )}
        </UIAlertDialogHeader>
        <UIAlertDialogFooter>
          <UIAlertDialogCancel onClick={onCancel}>{cancelText}</UIAlertDialogCancel>
          <UIAlertDialogAction onClick={onConfirm}>{confirmText}</UIAlertDialogAction>
        </UIAlertDialogFooter>
      </UIAlertDialogContent>
    </UIAlertDialog>
  )
}

UIAlertDialogInfo.displayName = "UIAlertDialogInfo"

// ========================================
// Type Exports
// ========================================

export type UIAlertDialogProps = React.ComponentPropsWithoutRef<typeof UIAlertDialog>
export type UIAlertDialogTriggerProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogTrigger>
export type UIAlertDialogContentProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogContent>
export type UIAlertDialogHeaderProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogHeader>
export type UIAlertDialogFooterProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogFooter>
export type UIAlertDialogTitleProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogTitle>
export type UIAlertDialogDescriptionProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogDescription>
export type UIAlertDialogActionProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogAction>
export type UIAlertDialogCancelProps = React.ComponentPropsWithoutRef<typeof UIAlertDialogCancel>